import { ActionIcon, Badge, Flex, Menu, NumberFormatter, Pagination, Switch, Table } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import * as C from '../../../../../ui/common/CommonStyles';
import { useNavigate } from 'react-router-dom';
import { cleanParams } from '../../../../../../shared/utils/cleanParams';
import { filterAiAgentTargetServices } from '../../../../../../shared/utils/aiAgentServiceChannel';
import { buildSearchString } from '../../utils/buildSearchString';
import { handleChangeLocaleTime } from '../../../../../../shared/utils/time';
import usePatchFeedback from '../hooks/usePatchFeedback';

interface IFormattedNumberProps {
  value: number | null | undefined;
}

interface IUsageLimitProps {
  used: number | null | undefined;
  limit: number | null | undefined;
}

function FormattedNumber(props: IFormattedNumberProps) {
  const { value } = props;

  return <>{value === null || value === undefined ? '-' : <NumberFormatter value={value} thousandSeparator />}</>;
}

function UsageLimit(props: IUsageLimitProps) {
  const { used, limit } = props;

  return (
    <>
      <FormattedNumber value={used} /> / <FormattedNumber value={limit} />
    </>
  );
}

export default function CompanyListTable(props: any) {
  const { companyList, totalPage, page, setSearchParams, searchParams, onOpen } = props;

  const navigate = useNavigate();
  const { mutate: patchFeedback } = usePatchFeedback();
  return (
    <>
      <C.TableWrap>
        <Table.ScrollContainer minWidth={1560}>
          <Table
            withTableBorder
            withColumnBorders
            highlightOnHover
            styles={{
              th: { textAlign: 'center' },
              td: { textAlign: 'center' },
            }}
          >
            <Table.Thead>
              {/* 1단 헤더 */}
              <Table.Tr>
                <Table.Th rowSpan={2} ta="center">
                  회사코드
                </Table.Th>
                <Table.Th rowSpan={2} ta="center">
                  거래처명
                </Table.Th>
                <Table.Th rowSpan={2} ta="center">
                  계약된 <br />
                  그룹 수
                </Table.Th>
                <Table.Th rowSpan={2} ta="center">
                  사용중인 서비스
                </Table.Th>
                {/* 그룹 헤더 */}
                <Table.Th rowSpan={2} ta="center">
                  학습 페이지 제한 수 <br /> (사용/MAX)
                </Table.Th>
                <Table.Th colSpan={3} ta="center">
                  계정 제한 수 <br />
                  (생성/MAX)
                </Table.Th>
                <Table.Th colSpan={3} ta="center">
                  질답 한도
                </Table.Th>
                <Table.Th rowSpan={2} ta="center">
                  사전 제한 수<br />
                  (생성/MAX)
                </Table.Th>
                <Table.Th rowSpan={2} ta="center">
                  피드백 관리
                </Table.Th>
                <Table.Th rowSpan={2} ta="center">
                  최종 수정일시
                </Table.Th>
                <Table.Th rowSpan={2} ta="center">
                  관리
                </Table.Th>
              </Table.Tr>

              {/* 2단 헤더 */}
              <Table.Tr>
                <Table.Th ta="center">SCAUTR</Table.Th>
                <Table.Th ta="center">PROGIX</Table.Th>
                <Table.Th ta="center" style={{ borderRight: '1px solid var(--table-border-color)' }}>
                  PRO-DOQ
                </Table.Th>
                <Table.Th ta="center">회사 월 제한</Table.Th>
                <Table.Th ta="center">회사 일 제한</Table.Th>
                <Table.Th ta="center" style={{ borderRight: '1px solid var(--table-border-color)' }}>
                  계정 일 제한
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {companyList.length > 0 ? (
                companyList.map((item: any) => {
                  return (
                    <Table.Tr key={item.companyId}>
                      <Table.Td>{item.companyId}</Table.Td>
                      <Table.Td
                        ta="left"
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={() => {
                          navigate(
                            `/admin/management/aiagent/company/${item.companyId}/model${buildSearchString({
                              companyName: item.companyName,
                            })}`,
                          );
                        }}
                      >
                        {item.companyName}
                      </Table.Td>
                      <Table.Td>{item.completedModelCount}</Table.Td>
                      <Table.Td>
                        {filterAiAgentTargetServices(item?.channels).map((channel) => (
                          <Badge
                            key={channel}
                            variant="light"
                            color={channel === 'SCAUTR' ? 'green' : channel === 'PROGIX' ? 'blue' : 'navy'}
                            mr={4}
                          >
                            {channel}
                          </Badge>
                        ))}
                      </Table.Td>
                      <Table.Td>
                        <UsageLimit used={item.learnedPageUsedCount} limit={item.pageLimit} />
                      </Table.Td>
                      <Table.Td>
                        <UsageLimit used={item.scautrAccountUsedCount} limit={item.accountLimit} />
                      </Table.Td>
                      <Table.Td>
                        <UsageLimit used={item.progixAccountUsedCount} limit={item.accountLimit} />
                      </Table.Td>
                      <Table.Td>
                        <UsageLimit used={item.prodoqAccountUsedCount} limit={item.accountLimit} />
                      </Table.Td>
                      <Table.Td>
                        <FormattedNumber value={item.companyChatTurnLimitMonthly} />
                      </Table.Td>
                      <Table.Td>
                        <FormattedNumber value={item.companyChatTurnLimitDaily} />
                      </Table.Td>
                      <Table.Td>
                        <FormattedNumber value={item.userChatTurnLimitDaily} />
                      </Table.Td>
                      <Table.Td>
                        <UsageLimit used={item.dictionaryUsedCount} limit={item.dictionaryLimit} />
                      </Table.Td>
                      <Table.Td>
                        <Flex justify="center">
                          <Switch
                            checked={item.feedbackManageYn === 'Y'}
                            onChange={() => {
                              patchFeedback({
                                companyId: item.companyId,
                                data: {
                                  feedbackManageYn: item.feedbackManageYn === 'Y' ? 'N' : 'Y',
                                },
                              });
                            }}
                          />
                        </Flex>
                      </Table.Td>
                      <Table.Td>{handleChangeLocaleTime(item.modifiedAt)}</Table.Td>
                      <Table.Td>
                        <Menu shadow="md" withArrow width={120} position="bottom-end" offset={4} withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="default">
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => onOpen(item.companyId)}>기본 설정</Menu.Item>
                            <Menu.Item
                              onClick={() =>
                                navigate(
                                  `/admin/management/aiagent/company/${item.companyId}/operation/history?companyName=${item.companyName}`,
                                )
                              }
                            >
                              이력 조회
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={13} ta="center">
                    조회된 데이터가 없습니다.
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </C.TableWrap>
      <C.PaginationBox>
        <Pagination
          total={totalPage}
          onChange={(value) => {
            const currentParams = Object.fromEntries(searchParams.entries());
            setSearchParams(
              cleanParams({
                ...currentParams,
                page: String(value),
              }),
            );
          }}
          value={Number(page)}
        />
      </C.PaginationBox>
    </>
  );
}
